import { formatAmount } from "@/lib/utils/formatters";
import { deriveJobDurationMonths } from "../../../utils/incomeEmploymentValidator";
import type { EmploymentFormRow } from "../../../types/tabs";
import type {
  ApiRecord,
  IncomeAttachmentFile,
  IncomeDocumentationApplicationData,
} from "../../../types/incomeEmploymentApi";

export function isUnitedStatesCountry(country: unknown): boolean {
  const c = String(country ?? "")
    .trim()
    .toLowerCase();
  if (!c) return false;
  return (
    c === "usa" ||
    c === "us" ||
    c === "u.s." ||
    c === "u.s.a." ||
    c === "united states" ||
    c === "united states of america"
  );
}

export function incomeJobTestId(
  employmentType: string,
  rowIndex: number,
  slug: string,
  suffix: string,
): string {
  const block =
    employmentType === "previous_job"
      ? `previous-job-${rowIndex}`
      : `current-job-${rowIndex}`;
  return `application-income-tab-${block}-${slug}-${suffix}`;
}

// Calculate yearly income based on period
export const calculateYearlyIncome = (
  income: string | number,
  period: string,
): string => {
  const amount = parseFloat(String(income)) || 0;
  switch (period) {
    case "Per_Month":
      return formatAmount(amount * 12);
    case "Per_Week":
      return formatAmount(amount * 52);
    case "Per_Bi_Monthly":
      return formatAmount(amount * 24);
    case "Per_Year":
      return formatAmount(amount);
    default:
      return formatAmount(amount);
  }
};

// Calculate job duration in months — mirrors Angular jobPeriod():
//   monthsDiff = moment(endDate).diff(startDate, 'months')
//   job_duration = monthsDiff + 1
// Angular also returns 0 when start date is today or in the future.
export const calculateJobDuration = (startDate: string, endDate?: string): string => {
  const months = deriveJobDurationMonths(startDate, endDate, {
    treatAsPresentJob: !endDate,
  });
  return months === "" ? "" : `${months} Months`;
};

/** Sync computed months into form state for API + validator (Angular `job_duration` control). */
export const enrichOtherIncomeRow = (
  form: Record<string, unknown>,
): Record<string, unknown> & { job_duration: string } => {
  if (form.income_period === "One_Time") {
    return { ...form, job_duration: "0" };
  }
  const durationLabel = calculateJobDuration(
    String(form.job_start_date ?? ""),
    form.present_job ? undefined : String(form.job_end_date ?? ""),
  );
  const months = durationLabel.replace(/\s*Months?/i, "").trim();
  return { ...form, job_duration: months };
};

export const otherIncomeFieldClass = (hasError: boolean, base: string) =>
  hasError ? `${base} border-red-500 focus:ring-red-500` : base;

/** Matches `isPresentJob` in incomeEmploymentValidator — API may send bool, 1, or "1". */
export function isPresentJobFlag(value: unknown): boolean {
  return value === true || value === "1" || value === 1;
}

/** End date present → not active; else use present_job (Angular resident-application-add). */
export function parseOtherIncomePresentJob(income: {
  present_job?: unknown;
  job_end_date?: unknown;
  end_date?: unknown;
}): boolean {
  const endRaw = income.job_end_date ?? income.end_date;
  const hasEndDate =
    endRaw != null &&
    String(endRaw).trim() !== "" &&
    String(endRaw).trim().toLowerCase() !== "null";

  if (hasEndDate) return false;

  const presentRaw = income.present_job;
  if (
    presentRaw === "0" ||
    presentRaw === 0 ||
    presentRaw === false ||
    presentRaw === "false"
  ) {
    return false;
  }

  if (isPresentJobFlag(presentRaw)) return true;

  return presentRaw == null || String(presentRaw).trim() === "";
}

export const mapOtherIncomeFormsToApi = (forms: Record<string, unknown>[]) =>
  forms.map((form) => {
    const enriched = enrichOtherIncomeRow(form);
    return {
      job_id: String(enriched.job_id ?? ""),
      income_period: String(enriched.income_period ?? ""),
      source_name: String(enriched.source_name ?? ""),
      income: enriched.income ?? "",
      job_start_date: String(enriched.job_start_date ?? ""),
      job_end_date: enriched.present_job
        ? null
        : String(enriched.job_end_date ?? ""),
      present_job: enriched.present_job ? "1" : "0",
      job_duration:
        enriched.job_duration !== undefined && enriched.job_duration !== ""
          ? String(enriched.job_duration)
          : "0",
      check_types: enriched.check_types ?? "",
      other_proof_of_income: enriched.other_proof_of_income ?? "",
    };
  });

// job_distance from API — matches Angular *ngIf="job_distance?.value > 50"
export const parseJobDistance = (value: unknown): number => {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const n = parseFloat(String(value));
  return Number.isNaN(n) ? 0 : n;
};

export const firstPresent = (...values: unknown[]): string => {
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (normalized) return normalized;
  }
  return "";
};

export const buildPropertyAddressForDistance = (
  applicationData: ApiRecord,
  applicationAllData?: ApiRecord,
): string => {
  const propertyData = (applicationData?.property || {}) as ApiRecord;
  const allPropertyData = (applicationAllData?.property || {}) as ApiRecord;
  const propAddress = firstPresent(
    applicationData?.property_address,
    applicationAllData?.property_address,
    applicationData?.physicalAddress,
    applicationAllData?.physicalAddress,
    propertyData.property_address,
    allPropertyData.property_address,
    propertyData.address,
    allPropertyData.address,
  );
  const propCity = firstPresent(
    applicationData?.property_city,
    applicationAllData?.property_city,
    propertyData.property_city,
    allPropertyData.property_city,
    propertyData.city,
    allPropertyData.city,
  );
  const propState = firstPresent(
    applicationData?.property_state,
    applicationAllData?.property_state,
    propertyData.property_state,
    allPropertyData.property_state,
    propertyData.state,
    allPropertyData.state,
  );
  const propZipcode = firstPresent(
    applicationData?.property_zipcode,
    applicationAllData?.property_zipcode,
    propertyData.property_zipcode,
    allPropertyData.property_zipcode,
    propertyData.zipcode,
    allPropertyData.zipcode,
    propertyData.zip_code,
    allPropertyData.zip_code,
  );

  return [propAddress, propCity, propState, propZipcode]
    .filter((part) => String(part ?? "").trim())
    .join(" ")
    .trim();
};

export const buildEmployerAddressForDistance = (
  row: Pick<
    EmploymentFormRow,
    | "employer_street_address"
    | "company_address"
    | "company_city"
    | "company_state"
    | "company_zipcode"
  >,
): string => {
  const detailAddress = [
    row.company_address,
    row.company_city,
    row.company_state,
    row.company_zipcode,
  ]
    .filter((part) => String(part ?? "").trim())
    .join(" ")
    .trim();

  return detailAddress || String(row.employer_street_address ?? "").trim();
};

export const buildGoogleMapsDirectionsUrl = (
  propertyAddress: string,
  employerAddress: string,
): string =>
  `https://www.google.com/maps/dir/${encodeURIComponent(propertyAddress)}/${encodeURIComponent(employerAddress)}`;

export const parseComparableDate = (value: unknown): Date | null => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [month, day, year] = raw.split("/").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isEndDateBeforeStartDate = (startValue: unknown, endValue: unknown): boolean => {
  const start = parseComparableDate(startValue);
  const end = parseComparableDate(endValue);
  if (!start || !end) return false;
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return end.getTime() < start.getTime();
};

/** Yearly income for display — matches Angular `jobIncome()`. */
export function angularJobIncomeYearly(
  income: unknown,
  incomePeriod: string | undefined,
): number {
  const amount = parseFloat(String(income ?? "").replace(/,/g, "")) || 0;
  if (!incomePeriod || amount === 0) return 0;
  switch (incomePeriod) {
    case "Per_Week":
      return amount * 52;
    case "Per_Bi_Monthly":
      return amount * 6;
    case "Per_Month":
      return amount * 12;
    case "Per_Year":
    case "One_Time":
      return amount;
    default:
      return amount;
  }
}

export function formatEmployerAddressAngular(employmentData: Record<string, unknown>): string {
  const street =
    (employmentData.employer_street as string) ||
    (employmentData.employer_street_address as string) ||
    (employmentData.company_address as string) ||
    "";
  const city =
    (employmentData.employer_city as string) ||
    (employmentData.company_city as string) ||
    "";
  const state =
    (employmentData.employer_state as string) ||
    (employmentData.company_state as string) ||
    "";
  const zip =
    (employmentData.employer_zip_code as string) ||
    (employmentData.company_zipcode as string) ||
    "";
  const country =
    (employmentData.employer_country as string) ||
    (employmentData.company_country as string) ||
    "";
  const parts = [street, city, state, zip, country]
    .map((p) => String(p || "").trim())
    .filter(Boolean);
  return parts.join(", ");
}

export function monthsAtJobDisplay(employmentData: {
  job_duration?: unknown;
  job_start_date?: string;
  start_date?: string;
  job_end_date?: string;
  end_date?: string;
}): string {
  if (
    employmentData.job_duration != null &&
    String(employmentData.job_duration).trim() !== ""
  ) {
    return String(employmentData.job_duration);
  }
  const s = calculateJobDuration(
    employmentData.job_start_date || employmentData.start_date || "",
    employmentData.job_end_date || employmentData.end_date,
  );
  return s.replace(/\s*Months?\s*$/i, "").trim();
}

/** Normalize API attachment lists that may arrive as arrays or keyed objects. */
export function normalizeIncomeAttachmentList(
  value: unknown,
): IncomeAttachmentFile[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is IncomeAttachmentFile =>
        item != null && typeof item === "object",
    );
  }
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).filter(
      (item): item is IncomeAttachmentFile =>
        item != null && typeof item === "object",
    );
  }
  return [];
}

export function buildIncomeVerificationDocuments(applicationData?: {
  ninja_income_proof?: unknown;
  proof_of_income?: unknown;
} | null): IncomeAttachmentFile[] {
  return [
    ...normalizeIncomeAttachmentList(applicationData?.ninja_income_proof).map(
      (file) => ({ ...file, _source: "ninja" as const }),
    ),
    ...normalizeIncomeAttachmentList(applicationData?.proof_of_income).map(
      (file) => ({ ...file, _source: "proof" as const }),
    ),
  ];
}

export function formatIncomeAttachmentCreatedAt(
  createdAt: string | number | undefined,
): string {
  if (createdAt == null || `${createdAt}`.trim() === "") return "";

  const createdAtNum = Number(createdAt);
  if (!Number.isFinite(createdAtNum)) {
    return `${createdAt}`.trim();
  }

  const date = new Date(
    createdAtNum > 1_000_000_000_000 ? createdAtNum : createdAtNum * 1000,
  );
  if (Number.isNaN(date.getTime())) {
    return `${createdAt}`.trim();
  }

  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  const yyyy = date.getFullYear();
  const min = `${date.getMinutes()}`.padStart(2, "0");
  const ss = `${date.getSeconds()}`.padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";
  const hour12 = date.getHours() % 12 || 12;
  return `${mm}/${dd}/${yyyy} ${String(hour12).padStart(2, "0")}:${min}:${ss} ${ampm}`;
}

function trimPersonNamePart(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** applicant_first_name → user_data → first_name */
export function buildApplicantDisplayName(
  person?: {
    first_name?: string | null;
    last_name?: string | null;
    applicant_first_name?: string | null;
    applicant_last_name?: string | null;
    user_data?: {
      first_name?: string | null;
      last_name?: string | null;
    } | null;
  } | null,
): string {
  if (!person) return "";

  const applicantFirst = trimPersonNamePart(person.applicant_first_name);
  const nestedFirst = trimPersonNamePart(person.user_data?.first_name);
  const rootFirst = trimPersonNamePart(person.first_name);
  const first = applicantFirst || nestedFirst || rootFirst;
  if (!first) return "";

  const applicantLast = trimPersonNamePart(person.applicant_last_name);
  const nestedLast = trimPersonNamePart(person.user_data?.last_name);
  const rootLast = trimPersonNamePart(person.last_name);
  const last = applicantLast || nestedLast || rootLast;
  return `${first} ${last}`.trim();
}

/** Prefer attachment `name`, then applicantName. */
export function formatIncomeAttachmentUploadMeta(
  file: { filename?: string; name?: string; uploaded_by?: string; created_at?: string | number },
  applicantName: string,
): { displayName: string; uploadedMetaText: string } {
  const displayName =
    trimPersonNamePart(file.filename) || "document";
  const uploadedByRaw =
    trimPersonNamePart(file.name) || trimPersonNamePart(applicantName);
  const uploadedBy =
    uploadedByRaw !== "" && uploadedByRaw !== displayName ? uploadedByRaw : "";

  let uploadedAtText = "";
  if (file.created_at != null && `${file.created_at}`.trim() !== "") {
    const createdAtNum = Number(file.created_at);
    if (Number.isFinite(createdAtNum)) {
      const d = new Date(
        createdAtNum > 1_000_000_000_000 ? createdAtNum : createdAtNum * 1000,
      );
      if (!Number.isNaN(d.getTime())) {
        const mm = `${d.getMonth() + 1}`.padStart(2, "0");
        const dd = `${d.getDate()}`.padStart(2, "0");
        const yyyy = d.getFullYear();
        const min = `${d.getMinutes()}`.padStart(2, "0");
        const ss = `${d.getSeconds()}`.padStart(2, "0");
        const ampm = d.getHours() >= 12 ? "PM" : "AM";
        const hour12 = d.getHours() % 12 || 12;
        uploadedAtText = `${mm}/${dd}/${yyyy} ${String(hour12).padStart(2, "0")}:${min}:${ss} ${ampm}`;
      } else {
        uploadedAtText = `${file.created_at}`.trim();
      }
    } else {
      uploadedAtText = `${file.created_at}`.trim();
    }
  }

  const metaParts = ["Uploaded", uploadedBy, uploadedAtText].filter(Boolean);
  return { displayName, uploadedMetaText: metaParts.join(" - ") };
}

function hasIncomeAttachmentId(file: IncomeAttachmentFile): boolean {
  return file.attachment_id != null && `${file.attachment_id}`.trim() !== "";
}

export function getNinjaIncomeProofViewFiles(
  applicationData?: { ninja_income_proof?: unknown } | null,
): IncomeAttachmentFile[] {
  return normalizeIncomeAttachmentList(applicationData?.ninja_income_proof);
}

/** Angular resident-application-add edit: ninja rows require attachment_id. */
export function getNinjaIncomeProofEditFiles(
  applicationData?: { ninja_income_proof?: unknown } | null,
): IncomeAttachmentFile[] {
  return getNinjaIncomeProofViewFiles(applicationData).filter(hasIncomeAttachmentId);
}

/** Angular application-details view: proof rows render only when filepath exists. */
export function getProofOfIncomeViewFiles(
  applicationData?: { proof_of_income?: unknown } | null,
): IncomeAttachmentFile[] {
  return normalizeIncomeAttachmentList(applicationData?.proof_of_income).filter(
    (file) => Boolean(file.filepath),
  );
}

/** Angular resident-application-add edit: all proof_of_income rows with attachment_id. */
export function getProofOfIncomeEditFiles(
  applicationData?: { proof_of_income?: unknown } | null,
): IncomeAttachmentFile[] {
  return normalizeIncomeAttachmentList(applicationData?.proof_of_income).filter(
    hasIncomeAttachmentId,
  );
}

export function getStoredNinjaIncomeProofCount(
  applicationData?: { ninja_income_proof?: unknown } | null,
): number {
  return normalizeIncomeAttachmentList(applicationData?.ninja_income_proof).length;
}

export function getStoredProofOfIncomeCount(
  applicationData?: { proof_of_income?: unknown } | null,
): number {
  return normalizeIncomeAttachmentList(applicationData?.proof_of_income).length;
}

export function hasProofOfIncomeFiles(
  applicationData?: { proof_of_income?: unknown } | null,
): boolean {
  return normalizeIncomeAttachmentList(applicationData?.proof_of_income).length > 0;
}

/** Angular application-details Income Documentation row: filename - timestamp. */
export function formatIncomeDocumentationViewMeta(file: {
  id?: string | number;
  filename?: string;
  name?: string;
  created_at?: string | number;
}): { displayName: string; uploadedMetaText: string } {
  const displayName = `${file.id ?? ""}${file.filename || file.name || "document"}`;
  const uploadedAtText = formatIncomeAttachmentCreatedAt(file.created_at);
  return {
    displayName,
    uploadedMetaText: uploadedAtText,
  };
}

/** Angular `supportingIncomeUploadMode`: AWAITING → resident_application; else → no_bank_ninja. */
export type SupportingIncomeUploadMode = "resident" | "ninja" | null;

export function resolveSupportingIncomeUploadMode(
  applicationData?: { applicant_status?: string } | null,
  isEditable = true,
): SupportingIncomeUploadMode {
  if (!applicationData || !isEditable) return null;
  return applicationData.applicant_status === "AWAITING" ? "resident" : "ninja";
}

/** Angular `showSupportingUploadsSection`: AWAITING or any stored INCOME / NINJA_INCOME files. */
export function showSupportingUploadsSection(
  applicationData?: IncomeDocumentationApplicationData | null,
): boolean {
  if (!applicationData) return false;
  if (applicationData.applicant_status === "AWAITING") return true;
  if (
    Array.isArray(applicationData.proof_of_income) &&
    applicationData.proof_of_income.length > 0
  ) {
    return true;
  }
  if (
    Array.isArray(applicationData.ninja_income_proof) &&
    applicationData.ninja_income_proof.length > 0
  ) {
    return true;
  }
  return false;
}

/** Angular application-details: Income Documentation when AWAITING or proof_of_income exists. */
export function showIncomeDocumentationSection(
  applicationData?: IncomeDocumentationApplicationData | null,
): boolean {
  if (!applicationData) return false;
  if (applicationData.applicant_status === "AWAITING") return true;
  return hasProofOfIncomeFiles(applicationData);
}

/** Show Verified Income block when underwriter amount set or is_approved_require === '0' (Angular *ngIf). */
export function showVerifiedIncomeSummary(
  applicationData?: {
    underwriter_verified_income?: unknown;
    is_approved_require?: unknown;
  } | null,
): boolean {
  if (!applicationData) return false;
  return (
    Boolean(applicationData.underwriter_verified_income) ||
    applicationData.is_approved_require === "0"
  );
}

/** Verified Income display uses underwriter_verified_income, not underwriter_verified_income_other (Angular). */
export function resolveIncomeTabVerifiedIncome(
  applicationData?: { underwriter_verified_income?: unknown } | null,
): number {
  const candidate = applicationData?.underwriter_verified_income;
  if (candidate === undefined || candidate === null || candidate === "") {
    return 0;
  }
  const num = Number(candidate);
  return Number.isFinite(num) ? num : 0;
}
